import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getAccessMatrix,
  revokeMember,
  setFeatureAccess,
  setPageAccess,
} from "../api/reports";
import { ApiError } from "../api/client";
import type { AccessMatrix } from "../types";
import { Avatar, Toggle } from "../components/primitives";
import { PageHeader } from "../components/Chrome";
import { toast } from "../components/Toast";

// A3 — Person detail with the access matrix (page + feature grants).
// This is the heart of the operator console.
export function AdminPersonDetail({ tenantSlug }: { tenantSlug: string }) {
  const { memberId } = useParams<{ memberId: string }>();
  const id = Number(memberId);
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // "page-{id}" | "feature-{id}"

  useEffect(() => {
    if (!Number.isFinite(id)) {
      navigate("/admin/people", { replace: true });
      return;
    }
    void (async () => {
      try {
        setMatrix(await getAccessMatrix(tenantSlug, id));
      } catch (e) {
        setError(e instanceof ApiError ? e.detail : (e as Error).message);
      }
    })();
  }, [tenantSlug, id, navigate]);

  const onPageToggle = async (pageId: number, granted: boolean) => {
    setBusy(`page-${pageId}`);
    try {
      const next = await setPageAccess(tenantSlug, id, pageId, granted);
      setMatrix(next);
      toast(granted ? "Page granted" : "Page revoked");
    } catch (e) {
      toast(e instanceof ApiError ? e.detail : (e as Error).message, "err");
    } finally {
      setBusy(null);
    }
  };

  const onFeatureToggle = async (featureId: number, granted: boolean) => {
    setBusy(`feature-${featureId}`);
    try {
      const next = await setFeatureAccess(tenantSlug, id, featureId, granted);
      setMatrix(next);
    } catch (e) {
      toast(e instanceof ApiError ? e.detail : (e as Error).message, "err");
    } finally {
      setBusy(null);
    }
  };

  const onRevoke = async () => {
    if (!window.confirm("Revoke this person's membership? They will lose access immediately.")) {
      return;
    }
    try {
      await revokeMember(tenantSlug, id);
      toast("Person revoked");
      navigate("/admin/people", { replace: true });
    } catch (e) {
      toast(e instanceof ApiError ? e.detail : (e as Error).message, "err");
    }
  };

  if (error) return <div className="container"><div className="empty err">{error}</div></div>;
  if (!matrix) return <div className="container"><div className="empty">Loading…</div></div>;

  return (
    <div className="container">
      <PageHeader title={matrix.member_email}>
        <Link to="/admin/people" className="btn-link">← People</Link>
        <button className="btn-danger" onClick={onRevoke}>
          Revoke
        </button>
      </PageHeader>
      <div className="person-card">
        <Avatar name={matrix.member_email} size={48} brand />
        <div>
          <div className="person-name">{matrix.member_email}</div>
        </div>
      </div>

      <h2 className="section-title">Page access</h2>
      {matrix.pages.length === 0 && (
        <div className="empty">
          No pages defined for this tenant yet. Create one in Pages.
        </div>
      )}
      <div className="page-access-list">
        {matrix.pages.map((p) => (
          <div key={p.id} className="page-access-row">
            <div className="page-access-head">
              <div>
                <div className="page-access-name">{p.name}</div>
                {p.description && (
                  <div className="page-access-desc">{p.description}</div>
                )}
              </div>
              <Toggle
                value={p.granted}
                partial={
                  p.granted && p.features.some((f) => !f.granted)
                }
                onChange={(v) => void onPageToggle(p.id, v)}
              />
            </div>
            {p.granted && p.features.length > 0 && (
              <div className="feature-list">
                {p.features.map((f) => (
                  <div
                    key={f.id}
                    className={
                      "feature-row" + (busy === `feature-${f.id}` ? " busy" : "")
                    }
                  >
                    <div className="feature-name">{f.name}</div>
                    <Toggle
                      value={f.granted}
                      onChange={(v) => void onFeatureToggle(f.id, v)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
